import { useMutation } from "@apollo/client";
import { useCallback, useState } from "react";

import AiChatAvatarStatic from "components/AiChat/Avatar/Static";
import FlashContext from "components/FlashMessages/FlashContext";
import { TickCircle } from "components/icons/generated";
import BaseActionButton from "ds/components/BaseAction/Button";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { showAcceptToSModal } from "../AcceptToSModal";
import { REQUEST_INFRA_ASSISTANT_ENABLING, SET_SPACELIFT_INTELLIGENCE_ENABLED } from "../gql";
import styles from "./styles.module.css";

type EnableAiFeaturesProps = {
  heading: string;
  description: string;
  buttonLabel: string;
};

const EnableAiFeatures = ({ heading, description, buttonLabel }: EnableAiFeaturesProps) => {
  const { viewer } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const isAdmin = viewer.admin;

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const [enableSpaceliftIntelligence, { loading: enabling }] = useMutation(
    SET_SPACELIFT_INTELLIGENCE_ENABLED,
    { refetchQueries: ["GetAISettings"] }
  );

  const [requestEnabling, { loading: requesting }] = useMutation(REQUEST_INFRA_ASSISTANT_ENABLING);

  const handleEnable = useCallback(async () => {
    try {
      const { errors } = await enableSpaceliftIntelligence({
        variables: { enabled: true },
      });
      if (errors?.length) throw errors;
      reportSuccess({ message: "Spacelift Intelligence enabled" });
    } catch (e) {
      onError(e);
    }
  }, [enableSpaceliftIntelligence, onError, reportSuccess]);

  const handleRequestEnabling = useCallback(async () => {
    try {
      const { errors } = await requestEnabling();
      if (errors?.length) throw errors;
      setRequestSent(true);
      reportSuccess({ message: "Request sent to your organization admins" });
    } catch (e) {
      onError(e);
    }
  }, [onError, reportSuccess, requestEnabling]);

  return (
    <Box direction="column" gap="x-large" align="center" justify="center" padding="x-large">
      <AiChatAvatarStatic size="large" />

      <Box direction="column" gap="medium" align="center">
        <Typography tag="p" variant="p-t6" align="center">
          {heading}
        </Typography>
        <Typography tag="p" variant="p-body3-multiline" align="center">
          {description}
        </Typography>
      </Box>

      {isAdmin && (
        <>
          <Toggle
            variant="checkbox"
            isSelected={termsAccepted}
            onChange={setTermsAccepted}
            aria-label="Accept Spacelift Intelligence Terms of Use"
            align="center"
          >
            <Typography tag="span" variant="p-t8">
              I have read and accept Spacelift Intelligence{" "}
              <BaseActionButton onPress={showAcceptToSModal} className={styles.termsLink}>
                Terms of Use
              </BaseActionButton>
            </Typography>
          </Toggle>
          <Button
            variant="primary"
            size="small"
            onPress={handleEnable}
            disabled={!termsAccepted || enabling}
          >
            {buttonLabel}
          </Button>
        </>
      )}

      {!isAdmin && !requestSent && (
        <Button
          variant="secondary"
          size="small"
          onPress={handleRequestEnabling}
          disabled={requesting}
        >
          Ask your Admin to accept T&amp;C
        </Button>
      )}

      {!isAdmin && requestSent && (
        <Box gap="small" align="center">
          <Icon src={TickCircle} color="success" />
          <Typography tag="p" variant="p-body3" color="success">
            Request sent to admin
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EnableAiFeatures;
